import axios from "axios";

/**
 * Reports v2 REST client
 * ======================
 * Talks to the backend report pipeline (mounted at /api/v2/reports in
 * backend/src/app.ts) instead of building the Excel file inside a dashboard
 * tab. The backend generates the workbook, uploads it to S3 and hands out a
 * 15-minute presigned download URL — no more gateway timeouts or a frozen UI.
 *
 * Auth mirrors src/graphql/apollo-client.js: the JWT stored in localStorage
 * `x-auth-token` is sent as the `x-auth-token` header; the backend verifies it
 * with JWT_PRIVATE_KEY (payload { _id, email }).
 *
 * The origin comes from utils/apiBaseUrl.js (REACT_APP_REPORTS_API_URL, else
 * the local backend when the page is served from localhost, else
 * REACT_APP_GRAPHQL_URI with /graphql stripped) — so REST and GraphQL always
 * target the same backend. Previously this read REACT_APP_GRAPHQL_URI verbatim,
 * which points at PRODUCTION from dashboard/.env while local GraphQL traffic
 * went to localhost:3000, so every report request 404'd locally.
 */

import { getApiBaseUrl } from "./apiBaseUrl";

const baseURL = getApiBaseUrl();

const http = axios.create({
  baseURL,
  timeout: 15000,
});

http.interceptors.request.use((config) => {
  const token = localStorage.getItem("x-auth-token");
  if (token) {
    // eslint-disable-next-line no-param-reassign
    config.headers["x-auth-token"] = token;
  }
  return config;
});

/**
 * Turns an unhelpful 404 into an actionable message.
 *
 * The report routes answer 404 with a JSON `{ error }` body for a missing job,
 * so anything else (an HTML 404 from a host that has no /api/v2/reports — e.g. a
 * deployed backend without the v2 code) is reported as a wrong-target error
 * instead of looking like an expired job.
 */
http.interceptors.response.use(undefined, (error) => {
  const status = error?.response?.status;
  const target = error?.config?.baseURL || baseURL;
  const body = error?.response?.data;

  if (status === 404 && !(body && typeof body === "object" && body.error)) {
    return Promise.reject(
      new Error(
        `Reports API not found at ${target} (404). That host has no /api/v2/reports — start the backend with the v2 report routes, or set REACT_APP_REPORTS_API_URL.`,
      ),
    );
  }

  if (!error?.response) {
    return Promise.reject(
      new Error(
        `Could not reach the reports API at ${target} (${error?.message || "network error"}).`,
      ),
    );
  }

  return Promise.reject(error);
});

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** POST /api/v2/reports/detailed -> 202 { reportId, status, reused } */
async function startDetailedReport({ companyId, start, end, site }) {
  const { data } = await http.post("/api/v2/reports/detailed", {
    companyId,
    start,
    end,
    site,
  });
  return {
    reportId: data.reportId,
    reused: !!data.reused,
    status: data.status,
  };
}

/** GET /api/v2/reports/detailed/:id -> { status, progress, error?, ready } */
async function getReportStatus(reportId) {
  const { data } = await http.get(`/api/v2/reports/detailed/${reportId}`);
  return data;
}

/**
 * Polls the job until it is done or failed.
 * onTick(job) fires after every poll so the UI can show progress.
 * Throws on failure, on 404 (job expired) or when the 10-minute budget is
 * exhausted (the job itself keeps running server-side; the user can retry
 * the download — single-flight means they may reattach to the same job).
 */
async function pollReportStatus(reportId, onTick) {
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  for (;;) {
    let job;
    try {
      job = await getReportStatus(reportId);
    } catch (err) {
      if (err?.response?.status === 404) {
        throw new Error("Report job expired — please generate it again.");
      }
      throw err;
    }
    if (typeof onTick === "function") onTick(job);
    if (job.status === "done" || job.status === "failed") {
      return job;
    }
    if (Date.now() > deadline) {
      throw new Error(
        "The report is taking longer than 10 minutes. It keeps running in the background — try again in a moment.",
      );
    }
    await sleep(POLL_INTERVAL_MS);
  }
}

/**
 * Fetches a 15-minute presigned S3 URL (JSON /url endpoint) and triggers the
 * browser download by navigating to it — plain top-level navigation, so no
 * S3 CORS configuration is involved.
 *
 * The saved file name comes from the Content-Disposition header the backend
 * signs into the URL (the S3 object key is the same human-readable name, e.g.
 * "Cato Networks_Detailed_Report_2026-08-28_to_2026-09-28_2026-09-28_12-24.xlsx").
 * `download` is set too as a hint for same-origin links; for cross-origin S3
 * links the browser ignores it and uses the header.
 *
 * Resolves to { url, fileName } so the caller can tell the user what was saved.
 */
async function downloadReport(reportId) {
  const { data } = await http.get(`/api/v2/reports/detailed/${reportId}/url`);
  const a = document.createElement("a");
  a.href = data.url;
  a.rel = "noopener";
  if (data.fileName) a.download = data.fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  return { url: data.url, fileName: data.fileName || null };
}

export default {
  baseURL,
  startDetailedReport,
  getReportStatus,
  pollReportStatus,
  downloadReport,
};
